import React, {useEffect, useState} from "react";
import {useParams, useNavigate} from "react-router";
import {useTranslation} from "react-i18next";
import Api from "../../services/Api";
import {getLanguage} from "../../lang/helpers";
import LocalBreadcrumbs from "../../components/Breadcrumbs/LocalBreadcrumbs";
import {
  VisionStatementAction,
  VisionStatementActivity,
  VisionStatementGoal,
  VisionStatementInterface,
  VisionStatementPartStatus,
  VsSurveyRequest,
} from "./interfaces";
import Stepper from "../../components/Stepper";
import CustomButton from "../../components/CustomButton";
import TwoBars from "../../components/Bars/TwoBars";
import PlainBadge from "../../components/Badges/PlainBadge";
import iconFlag from "url:shared-assets/icons/flag.svg";

import TabMenu from "../../components/TabMenu/TabMenu";
import {getTabNr, setTabNr} from "../../helpers/tabNavigation";
import iconCheck from "url:shared-assets/icons/checkmark.svg";
import iconClock from "url:shared-assets/icons/clock.svg";
import iconEmail from "url:shared-assets/icons/email_16x16.svg";
import iconEmailOpen from "url:shared-assets/icons/email-open.svg";
import iconCalendar from "url:shared-assets/icons/calendar.svg";
import iconCalendar2 from "url:shared-assets/icons/calendar-2.svg";
import {renderOnTrack} from "../../pages/DevelopmentPlan/helpers";
import {SurveyQuestionInterface, SurveyResultInterface} from "../../pages/SurveyResult/interfaces";
import SurveyQuestionsResult from "../../pages/SurveyResult/components/SurveyQuestionsResult";

interface ClientInterface {
  id: string;
  firstName: string;
  lastName: string;
}


const CoachVisionStatement = () => {
  const api = Api.getInstance();
  const navigate = useNavigate();
  const {t, i18n} = useTranslation(); // i18n translation hook
  const lang = getLanguage(i18n.language);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tab, setTab] = useState(getTabNr());
  const [goals, setGoals] = useState<VisionStatementGoal[]>([]);
  const [activities, setActivities] = useState<VisionStatementActivity[]>([]);
  const [viewGoal, setViewGoal] = useState<VisionStatementGoal | null>(null);
  const [viewAction, setViewAction] = useState<VisionStatementAction | null>(null);
  const [viewActivity, setViewActivity] = useState<VisionStatementActivity | null>(null);
  const [surveyRequestNr, setSurveyRequestNr] = useState(0);
  const [client, setClient] = useState<ClientInterface>({
    id: "",
    firstName: "",
    lastName: "",
  });
  const [visionStatement, setVisionStatement] = useState<VisionStatementInterface>({
    id: "",
    surveyId: null,
    title: "",
    description: "",
    deadline: "",
    progress: 0,
    development: 0,
    effort: 0,
    onTrack: 0,
    status: 2,
    avgGap: 0,
    questions: [],
    surveyRequests: [],
    customerCategoryId: "",
    goalsTotal: 0,
    goalsCompleted: 0,
    activitiesCompleted: 0,
    activitiesTotal: 0
  });
  const { id } = useParams();
  const today = new Date();
  const todayDate = today.toISOString().substring(0, 10);

  useEffect(() => {
    getData();
  }, []);

  useEffect(() => {
    // When we reload data after a change of goal or action, check if we need to update viewGoal or viewAction
    if (viewGoal) {
      let foundGoal = false;
      for (let goal of goals) {
        if (viewGoal.id === goal.id) {
          setViewGoal(goal);
          foundGoal = true;
          if (viewAction) {
            let found = false;
            for (let action of goal.actions) {
              if (viewAction.id === action.id) {
                setViewAction(action);
                found = true;
                break;
              }
            }
            if (!found) {
              // Assume it is deleted
              setViewAction(null);
            }
          }
          break;
        }
      }
      if (!foundGoal) {
        // Assume it was deleted
        setViewGoal(null);
      }
    } else if (viewActivity) {
      let found = false;
      for (let activity of activities) {
        if (viewActivity.id === activity.id) {
          setViewActivity(activity);
          found = true;
        }
      }
      if (!found) {
        // Assume it was deleted
        setViewActivity(null);
      }
    }
  }, [goals, activities])

  const changeTab = (_tab: number) => {
    setTab(_tab);
    setTabNr(_tab);
    if (_tab > 0) {
      setViewGoal(null);
      setViewAction(null);
      setViewActivity(null);
    }
  };

  const getData = () => {
    api.get("coach/client/vision_statement/" + id).then(response => {
      if (response.json.status === "ok") {
        const _visionStatement: VisionStatementInterface = response.json.visionStatement;
        setVisionStatement(_visionStatement);
        setGoals(response.json.goals);
        setActivities(response.json.activities);
        setClient(response.json.client);
      } else if (response.json.error) {
        setError(response.json.error);
      } else {
        setError("Unknown error");
      }
      setLoading(false);
    });
  }

  if (loading) {
    return (<div className="mt-4">Loading...</div>);
  }

  if (error.length > 0) {
    return (<div className="mt-4">Error: {error}</div>);
  }

  const renderSuccessPlan = () => {
    if (goals.length > 0 || activities.length > 0) {
      return (
        <>
          {
            goals.map((goal, i) => {
              return (
                <div key={"goal_" + i}>
                  {i === 0 && <div className="mt-4 z-title1">{t("sub-goals")}</div>}
                  <div className="mt-2 p-4 bg-indigo-800 rounded-md hover:bg-indigo-700 cursor-pointer"
                       onClick={() => {setViewGoal(goal); setViewActivity(null)}}>
                    <div className="flex items-center justify-between">
                      <div className="z-title1 flex items-center gap-x-2">
                        {goal.title}
                        {
                          goal.status === VisionStatementPartStatus.Done &&
                          <PlainBadge text={t("completed")} color="green" iconLeft={iconCheck} size="small"/>
                        }
                      </div>
                      <div>
                        <span className="z-title3">{t("due-date")}: </span>
                        <span className="text-sm text-gray-300">{goal.deadline}</span>
                      </div>
                    </div>
                    <div className="mt-2">
                      <span className="text-gray-100 font-medium">{lang === "sv" ? "Beteenden och uppgifter" : "Behaviours and actions"}: </span>
                      <span className="text-gray-300">
                        {goal.actions.filter(g => g.status === 1).length} {t("of")}&nbsp;
                        {goal.actions.length} {t("completed").toLowerCase()}
                      </span>

                    </div>
                  </div>
                </div>
              )
            })
          }

          {
            activities.map((activity, i) => {
              return (
                <div key={"activity_" + i}>
                  {i === 0 && <div className="mt-4 z-title1">{t("activities")}</div>}
                  <div className="mt-2 p-4 bg-indigo-800 rounded-md hover:bg-indigo-700 cursor-pointer"
                       onClick={() => {setViewActivity(activity); setViewGoal(null)}}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-x-2">
                        <div className="z-title1">{activity.title}</div>
                        {
                          activity.status === VisionStatementPartStatus.Done &&
                            <PlainBadge text={t("completed")} color="green" iconLeft={iconCheck} size="small"/>
                        }
                      </div>

                      <div>
                        <span className="z-title3">{t("due-date")}: </span>
                        <span className="text-sm text-gray-300">{activity.deadline}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )
            })
          }

        </>
      )
    }
    return (
      <div className="mt-4 bg-indigo-900 rounded-md text-center p-4">
        <p>
          {
            lang === "sv" ? "Börja bygga din framgångsplan med verktyg som för dig mot denna målbild." :
              "Start building your success plan with tools that move you towards this vision."
          }
        </p>

        <CustomButton buttonType="blueBase" className="mt-4 mx-auto" style={{width: "unset"}}
                      onClick={() => changeTab(1)}>
          {lang === "sv" ? "Utforska verktygslådan" : "Explore the toolkit"}
        </CustomButton>
      </div>
    )
  }

  const renderToolkit = () => {
    return (
      <div className="mt-4 flex flex-wrap gap-4">
        <div className="main-half-col x-small rounded-lg p-4 border border-indigo-800 flex items-center">
          <div className="h-14 w-14 bg-indigo-800 p-4 rounded-full shrink-0">
            <img src={iconFlag} alt="flag" className="h-6 w-6"/>
          </div>

          <div className="ml-4">
            <div className="font-medium text-gray-100">
              {lang === "sv" ? "Skapa ett delmål" : "Create a sub-goal"}
            </div>
            <div className="font-medium text-gray-400">
              {
                lang === "sv" ? "Bryt ner din målbild i uppnåeliga resultat." :
                  "Break down your vision into achievable milestones."
              }
            </div>
          </div>

        </div>

        <div className="main-half-col x-small rounded-lg p-4 border border-indigo-800 flex items-center">
          <div className="h-14 w-14 bg-indigo-800 p-4 rounded-full shrink-0">
            <img src={iconCalendar2} alt="flag" className="h-6 w-6"/>
          </div>

          <div className="ml-4">
            <div className="font-medium text-gray-100">
              {lang === "sv" ? "Skapa en aktivitet" : "Create an activity"}
            </div>
            <div className="font-medium text-gray-400">
              {
                lang === "sv" ? "Ta små steg mot din målbild." :
                  "Take small steps towards your vision."
              }
            </div>
          </div>
        </div>
      </div>
    )
  }

  const renderStepper = (surveyRequest: VsSurveyRequest, surveyResult: SurveyResultInterface) => {

    if (surveyRequest.deadline < todayDate) {
      return (
        <div className="mt-4 bg-indigo-800 rounded-sm p-4">
          Feedback request failed
        </div>
      )
    }

    let description1: string;
    let description2 = lang === "sv" ? "Be om feedback"
      : "Send a request to those around you";
    let description3 = lang === "sv" ? "Väntar på feedback"
      : "Waiting for feedback";

    let buttonText = "";
    let navigateTo = "";

    let step = 0;
    let completedSteps: Array<number> = []

    if (surveyResult.scoreSelf > 0) {
      completedSteps.push(0);
      step++;
      description1 = lang === "sv" ? "Klar" : "Completed";
    } else {
      description1 = lang === "sv" ? "Slutför din självskattning" : "Complete your self-assessment to continue";
      buttonText = lang === "sv" ? "Gör självskattning" : "Do self-assessment";
      navigateTo = "/feedback/survey/" + surveyRequest.id;
    }

    if (step > 0) {
      if (surveyResult.respondents.length > 0) {
        completedSteps.push(1);
        step++;
        description2 = lang === "sv" ? "Skickad" : "Sent";
      } else {
        buttonText = t("request-feedback");
        navigateTo = "/dev-plan/survey_request_invite/" + surveyRequest.id;
      }
    }

    const stepsData = [
      {
        title: t("self-assessment"),
        description: description1,
        content: null,
      },
      {
        title: t("feedback-request"),
        description: description2,
        content: null,
      },
      {
        title: lang === "sv" ? "Feedbackresultat och analys" : "Feedback result and analysis",
        description: description3,
        content: null,
      },
    ];


    return (
      <div className="mt-4 bg-indigo-800 rounded-sm px-4 pb-4">
        <Stepper
          steps={stepsData}
          direction="vertical"
          currentIndex={step}
          completedSteps={completedSteps}
        />

        {
          buttonText.length > 0 &&
            <div className="mt-4 flex justify-center">
                <CustomButton buttonType="blueBase" style={{width: "unset"}} onClick={() => navigate(navigateTo)}>
                  {buttonText}
                </CustomButton>
            </div>
        }

      </div>
    )
  }

  const renderFeedbackResult = () => {
    if (visionStatement.surveyRequests.length === 0) {
      return (
        <div className="mb-4 text-gray-400">
          {
            lang === "sv" ? "Inga klara feedbackresultat" : "No completed feedback requests"
          }
        </div>
      )
    }

    const surveyRequest = visionStatement.surveyRequests[surveyRequestNr];

    if (!surveyRequest.surveyResult) {
      return null;
    }

    let scoreSelf = 0;
    let scoreOther = 0;
    let selfNr = 0;
    let otherNr = 0;
    const surveyResult = surveyRequest.surveyResult;
    const questions: Array<SurveyQuestionInterface> = [];

    if (surveyRequest.surveyResult) {
      for (const focusArea of surveyResult.focusAreas) {
        for (const question of focusArea.questions) {
          questions.push(question);
          if (question.scoreSelf > 0) {
            scoreSelf += question.scoreSelf;
            selfNr++;
          }
          if (question.scoreOther > 0) {
            scoreOther += question.scoreOther;
            otherNr++;
          }
        }
      }
      if (selfNr) {
        scoreSelf /= selfNr;
      }
      if (otherNr) {
        scoreOther /= otherNr;
      }
    }

    // const questions = surveyResult.focusAreas[0].questions;

    return (
      <>
        {
          visionStatement.surveyRequests.length > 1 &&
            <div className="mb-4">
                <select className="z-select small" style={{width: "unset"}}
                        onChange={(e) => setSurveyRequestNr(parseInt(e.target.value))}
                        value={surveyRequestNr}>
                  {
                    visionStatement.surveyRequests.map((surveyRequest, i) => (
                      <option key={"sr_" + i} value={i}>{surveyRequest.created.substring(0, 10)}</option>
                    ))
                  }
                </select>
            </div>
        }

        <div className="bg-indigo-900 rounded-md p-4">
            <div className="flex justify-between">
                <div className="font-medium text-gray-100">{t("overview")}</div>
                <div className="flex items-center justify-center">
                    <div className="h-4 w-4 rounded-full bg-peach-500" onClick={() => getData()}></div>
                    <div className="text-sm ml-2 text-gray-100">{t("self-assessment")}</div>
                    <div className="h-4 w-4 rounded-full bg-peach-300 ml-4"></div>
                    <div className="text-sm ml-2 text-gray-100">Feedback</div>
                </div>
            </div>

            <div className="main-half-col small mt-2">
                <TwoBars value1={scoreSelf} value2={scoreOther}
                         results1={scoreSelf.toFixed(1) + "/5"}
                         results2={scoreOther.toFixed(1) + "/5"}
                />
            </div>

            <SurveyQuestionsResult questions={questions} lang={lang} surveyResult={surveyResult} feedbackType={0} clientName="Client name"/>

        </div>

        <div className="mt-6 z-title1">
          {t("details")}
        </div>

        <div className="mt-2 flex items-center">
          <img src={iconEmail} alt="sent" className="h-4 w-4 mr-2"/>
          <div className="z-title3">{t("request-sent")}:</div>
          <div className="text-sm text-gray-400">&nbsp;{surveyRequest.created.substring(0, 10)}</div>
        </div>

        <div className="mt-2 flex items-center">
          <img src={iconCalendar} alt="cal" className="h-4 w-4 mr-2"/>
          <div className="z-title3">{t("result-created-at")}:&nbsp;</div>
          <div className="text-sm text-gray-400">
            {surveyRequest.completed ? surveyRequest.completed.substring(0, 10) : t("pending")}
          </div>
        </div>

        <div className="mt-2 flex items-center">
          <img src={iconEmailOpen} alt="open" className="h-4 w-4 mr-2"/>
          <div className="z-title3">{t("received")}:</div>
          <div className="text-sm text-gray-400">
              &nbsp;{surveyResult.nrOtherResponses} {t("out-of")} {surveyResult.respondents.length}
          </div>
        </div>


      </>
    )
  }

  const renderFeedback = () => {
    return (
      <div className="mt-4">
        {renderFeedbackResult()}
      </div>
    )
  }

  const renderCreated = () => {
    const items = [
      {
        title: t("success-plan"),
        callback: () => changeTab(0),
      },
      {
        title: t("toolkit"),
          callback: () => changeTab(1),
      },
      {
        title: "Feedback",
        callback: () => changeTab(2),
      }
    ]

    return (
      <>
        {
          (visionStatement.progress !== 100 && visionStatement.deadline < todayDate) &&
            <div className="mt-4 border border-primary-500 bg-indigo-800 rounded-md p-4">
                <div className="flex items-center">
                    <img src={iconClock} alt="time" className="h-6 w-6"/>
                    <div className="font-medium ml-2">
                      {
                        lang === "sv" ? "Slutdatumet är här" : "Due date is here"
                      }
                    </div>
                </div>
                <div className="mt-2 text-sm text-gray-300">
                  {
                    lang === "sv" ? "Hur långt har du kommit? Uppdatera din status och fortsätt framåt." :
                      "Update your progress to move forward."
                  }
                </div>

            </div>
        }
        <div className="mt-4 flex items-center justify-between">
          <div className="flex gap-x-2 items-center">
            <div className="z-h2">{visionStatement.title}</div>
            {
              visionStatement.progress === 100 ? (
                <PlainBadge text={lang === "sv" ? "Klar" : "Completed"} color="green" size="small"/>
              ) : (
                <>
                  {renderOnTrack(visionStatement.onTrack, lang)}
                </>
              )
            }

          </div>
        </div>

        <div className="mt-4 z-title3 text-gray-300">{t("description")}</div>
        <div className="text-gray-400">
          {visionStatement.description}
        </div>

        <div className="mt-4 z-title3 text-gray-300">{t("due-date")}</div>
        <div className="text-gray-400">
          {visionStatement.deadline}
        </div>


        <div className="flex mt-6 items-center justify-between">
          <div className="h-2 rounded-full bg-indigo-700" style={{width: "calc(100% - 200px"}}>
            <div className="h-2 rounded-full bg-primary-500" style={{width: visionStatement.progress + "%"}}></div>
          </div>
          <div className="ml-1 text-sm gray-300">{visionStatement.progress}%</div>
        </div>

        <div className="mt-6">
          <TabMenu
            active={tab}
            items={items}
          />
        </div>

        {tab === 0 && renderSuccessPlan()}
        {tab === 1 && renderToolkit()}
        {tab === 2 && renderFeedback()}


      </>
    )
  }

  return (
    <div className="main-one-col small pb-4">

      <LocalBreadcrumbs crumbs={[
        {name: lang === "sv" ? "Hem" : "Home", url: "/"},
        {name: t('my-clients'), url: '/clients'},
        {name: client.firstName + " " + client.lastName, url: "/clients/" + client.id },
        {name: t("vision_statement")},
      ]}/>

      {
        renderCreated()
      }

    </div>
  )
}

export default CoachVisionStatement;